import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { Icon } from '../../Icon'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './Table'

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    emptyState?: React.ReactNode
    expandable?: boolean
    pinFirstColumn?: boolean
    getSubRows?: (row: TData) => (TData & { id: string | number })[] | undefined
}

// TODO: sorting, pagination

function DataTable<TData, TValue>({
    columns,
    data,
    emptyState,
    expandable = false,
    pinFirstColumn = false,
    getSubRows,
}: DataTableProps<TData, TValue>) {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    })

    const getPinnedClassName = (isFirstColumn: boolean, isHeader: boolean = false) => {
        if (!pinFirstColumn || !isFirstColumn) return ''

        let className =
            'sticky left-0 z-10 after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-gray-300 group-hover/body-table-row:bg-black-2'

        if (isHeader) {
            className = twMerge(className, 'bg-gray-100')
        }
        else {
            className = twMerge(className, 'bg-white hover:bg-gray-100')
        }

        return className
    }

    const toggleRow = (rowId: string) => {
        if (expandable) {
            setExpandedRows((prev) => ({
                ...prev,
                [rowId]: !prev[rowId],
            }))
        }
    }

    const renderSubRows = (row: TData, rowId: string) => {
        const subRows = getSubRows ? getSubRows(row) : []
        if (!expandedRows[rowId] || !subRows) return null

        return subRows?.map((subRow) => (
            <TableRow key={`${rowId}-${subRow.id}`} className="bg-black/2">
                {columns.map((column, columnIndex) => (
                    <TableCell
                        key={`${subRow.id}-${column.id || columnIndex}`}
                        className={twMerge(
                            getPinnedClassName(columnIndex === 0),
                            'bg-black-2 hover:bg-black-4'
                        )}
                    >
                        {columnIndex === 0 ? (
                            <div className="flex items-center">
                                <div className="ml-18" />
                                {flexRender<any>(column.cell, {
                                    row: { original: subRow },
                                })}
                            </div>
                        ) : (
                            flexRender<any>(column.cell, {
                                row: { original: subRow },
                            })
                        )}
                    </TableCell>
                ))}
            </TableRow>
        ))
    }

    const renderTableHeader = () => (
        <TableHeader data-testid="header">
            {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} location="head">
                    {headerGroup.headers.map((header, index) => (
                        <TableHead
                            key={header.id}
                            className={getPinnedClassName(index === 0, true)}
                        >
                            {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                    ))}
                </TableRow>
            ))}
        </TableHeader>
    )

    const renderTableBody = () => (
        <TableBody data-testid="body">
            {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                        <TableRow
                            onClick={() => toggleRow(row.id)}
                            className={expandable ? 'cursor-pointer' : ''}
                            data-testid="row-trigger"
                        >
                            {row.getVisibleCells().map((cell, index) => (
                                <TableCell
                                    key={cell.id}
                                    className={twMerge(
                                        'hover:bg-black/4',
                                        getPinnedClassName(index === 0)
                                    )}
                                >
                                    {index === 0 && expandable ? (
                                        <div className="flex items-center">
                                            {getSubRows && getSubRows(row.original)?.length && (
                                                <Icon
                                                    icon="chevronDown.small"
                                                    className={`mr-3 inline size-4 transition-transform duration-200 ${expandedRows[row.id] ? '-rotate-180' : 'rotate-0'}`}
                                                    data-testid="chevron-icon"
                                                />
                                            )}
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </div>
                                    ) : (
                                        flexRender(cell.column.columnDef.cell, cell.getContext())
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                        {expandable && expandedRows[row.id] && renderSubRows(row.original, row.id)}
                    </React.Fragment>
                ))
            ) : (
                <TableRow className="hover:bg-white">
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                        {emptyState}
                    </TableCell>
                </TableRow>
            )}
        </TableBody>
    )

    return (
        <Table data-testid="table">
            {renderTableHeader()}
            {renderTableBody()}
        </Table>
    )
}

export default DataTable
